import { StyleDeclarationValue } from 'aphrodite';
import { useEffect, useMemo, useState } from 'react';
import { PhoneNumberLabels } from '../../../../models';
import { Combobox, ComboboxContent, ComboboxInput, ComboboxItem, ComboboxList } from '../../../components-v2/Combobox';

const presetLabels = PhoneNumberLabels.map(l => l.title as string);

interface PhoneNumberLabelComboboxProps {
	label: string;
	onLabelChange: (label: string) => void;
	placeholder?: string;
	styles?: StyleDeclarationValue[];
}

export const PhoneNumberLabelCombobox = ({
	label,
	onLabelChange,
	placeholder = 'Label...',
	styles = [],
}: PhoneNumberLabelComboboxProps) => {
	const [inputValue, setInputValue] = useState(label || '');

	// Sync inputValue with label prop when it changes from parent
	useEffect(() => {
		setInputValue(label || '');
	}, [label]);

	// Check if current input matches a preset (case-insensitive)
	const isCustomLabel = useMemo(() => {
		const trimmed = inputValue.trim().toLowerCase();
		if (!trimmed) return false;
		return !presetLabels.some(presetLabel => presetLabel.toLowerCase() === trimmed);
	}, [inputValue]);

	const updateLabel = (newLabel: string) => {
		const trimmedLabel = newLabel.trim();
		onLabelChange(trimmedLabel);
	};

	const onLabelSelected = (value: unknown) => {
		if (value === null) {
			// Clear button was clicked
			setInputValue('');
			updateLabel('');
		} else if (typeof value === 'string') {
			setInputValue(value);
			updateLabel(value);
		}
	};

	const onInputValueChange = (value: string) => {
		setInputValue(value);
	};

	const onInputBlur = () => {
		// When the input loses focus, use whatever was typed as the label
		updateLabel(inputValue);
	};

	// Build items list - include custom option if user typed something not in presets
	const items = useMemo(() => {
		if (isCustomLabel) {
			return [inputValue.trim(), ...presetLabels];
		}
		return presetLabels;
	}, [isCustomLabel, inputValue]);

	// Only set value if inputValue matches label (selected state)
	// Otherwise null (typing state) to prevent input fighting
	const comboboxValue = inputValue === label ? label : null;

	return (
		<Combobox
			items={items}
			value={comboboxValue}
			onValueChange={onLabelSelected}
			onInputValueChange={onInputValueChange}
			inputValue={inputValue}
		>
			<ComboboxInput placeholder={placeholder} onBlur={onInputBlur} showClear styles={styles} />
			<ComboboxContent>
				<ComboboxList>
					{(item: string) => {
						// Show "Use [text]" for custom option, regular text for presets
						const isCustomOption = isCustomLabel && item === inputValue.trim();
						return (
							<ComboboxItem key={item} value={item}>
								{isCustomOption ? `Use "${item}"` : item}
							</ComboboxItem>
						);
					}}
				</ComboboxList>
			</ComboboxContent>
		</Combobox>
	);
};
